import React, { FC } from 'react';
import { Form, Formik } from 'formik';
import { addressSchema } from 'FeatureModules/Setting/schemas/company.schema';
import FormControlBar from 'Components/WelcomeSlides/FormControlBar';
import { useMutation, useQuery } from '@apollo/client';
import { Fade, styled, Box } from '@mui/material';
import { GET_COMPANY_DETAILS } from 'graphql/secure/queries';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import Loader from 'Components/Forms/Loader';
import mapSection from 'assets/img/welcomeSlides/map-section-berlin.png';
import { PageContainer, TextContainer, ImageContainer, Headline, SkipButton } from './styles';
import useWelcomeSlides from 'hooks/useWelcomeSlides';
import { UPDATE_COMPANY } from 'graphql/secure/mutations/company.mutations';
import ErrorState from 'Components/ErrorState';
import AddressFields from 'FeatureModules/Setting/Components/Forms/AddressFields';

interface IAddressFormValues {
  street: string;
  streetNumber: string;
  zipCode: string;
  city: string;
  country: string;
}

interface IAddressProps {
  active: boolean;
}

const StyledTextContainer = styled(TextContainer)(({ theme }) => ({
  width: '55%',
}));

const StyledImageContainer = styled(ImageContainer)(({ theme }) => ({
  width: '45%',
}));

const Address: FC<IAddressProps> = ({ active }) => {
  const { nextSlide } = useWelcomeSlides();
  const { logEvent } = useLogEvent();
  const {
    data: { company } = {},
    error,
    loading,
  } = useQuery(GET_COMPANY_DETAILS, {
    fetchPolicy: 'cache-only',
  });

  const { setSuccessMessage, setErrorMessage } = useMessages();
  const [updateCompany] = useMutation(UPDATE_COMPANY, {
    onCompleted: () => {
      setSuccessMessage('Success! Company details were updated.');
      logEvent('WELCOME_SLIDES_UPDATE_ADDRESS');
    },
    onError: () => {
      setErrorMessage("Something went wrong updating your company's details.");
    },
  });

  const handleSubmit = (input: IAddressFormValues) => {
    return updateCompany({
      variables: {
        input: {
          name: company?.name,
          website: company?.website,
          address: input,
        },
      },
    });
  };

  if (error) return <ErrorState error={error} />;
  if (!company && loading) return <Loader />;

  return (
    <Formik
      initialValues={{
        ...(company?.address || addressSchema.default()),
      }}
      validateOnBlur={false}
      validateOnChange={true}
      validationSchema={addressSchema}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, submitForm }) => (
        <>
          <PageContainer>
            <StyledTextContainer>
              <Headline variant="TITLE_LARGE">Company address</Headline>
              <Box mt={3}>
                <Form>
                  <AddressFields fullWidth />
                </Form>
              </Box>
            </StyledTextContainer>
            <Fade in={active} timeout={500}>
              <StyledImageContainer>
                <SkipButton onClick={nextSlide}>Add this later</SkipButton>
                <img alt="Map section of Berlin" src={mapSection} />
              </StyledImageContainer>
            </Fade>
          </PageContainer>
          <FormControlBar onSubmit={submitForm} isSubmitting={isSubmitting} />
        </>
      )}
    </Formik>
  );
};

export default Address;
